import { createContext, useContext, useEffect, useState, PropsWithChildren } from 'react';
import findLastIndex from 'lodash/fp/findLastIndex';
import pullAt from 'lodash/fp/pullAt';
import last from 'lodash/fp/last';
import { OverlayContext } from './overlayContext';

const noop = () => {};

type KeypressMapping = {
   id: string;
   key: string;
   action: () => void;
   overlayText?: string;
};

type KeyCodeMappings = Record<string, KeypressMapping[]>;

const keypressContextDefaults = {
   pushMapping: noop as (_item: KeypressMapping) => void,
   popMapping: noop as (_item: KeypressMapping) => void,
   pushMappings: noop as (_items: KeypressMapping[]) => void,
   popMappings: noop as (_items: KeypressMapping[]) => void,
};

const KeypressContext = createContext(keypressContextDefaults);

const setupKeydownListener = (keyCodeMappings: KeyCodeMappings) => {
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useContext" is called in "setupKeydownListener" that is neither a React function component nor a custom Hook ...
   const overlayContext = useContext(OverlayContext);

   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEffect" is called in "setupKeydownListener" that is neither a React function component nor a custom Hook ...
   useEffect(() => {
      const onKeydown = (event: { key: string }) => {
         const mappings = keyCodeMappings[event.key] || [];
         const lastMapping = last(mappings);
         if (!lastMapping) {
            return;
         }

         if (lastMapping.overlayText) {
            overlayContext.showWith(lastMapping.overlayText);
         }

         lastMapping.action();
      };

      document.addEventListener('keydown', onKeydown, true);

      return () => {
         document.removeEventListener('keydown', onKeydown, true);
      };
   }, [keyCodeMappings]);
};

/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
function KeypressActionProvider({ children }: PropsWithChildren<{}>) {
   const [keyCodeMappings, setKeyCodeMappings] = useState<KeyCodeMappings>(
      [] as unknown as KeyCodeMappings
   );

   setupKeydownListener(keyCodeMappings);

   const pushMapping = (item: KeypressMapping) => {
      const mappings = keyCodeMappings[item.key] || [];
      mappings.push(item);

      keyCodeMappings[item.key] = mappings;
      setKeyCodeMappings(keyCodeMappings);
   };

   const popMapping = (item: KeypressMapping) => {
      const mappings = keyCodeMappings[item.key] || [];

      const lastMatchIndex = findLastIndex(mapping => mapping.id == item.id, mappings);

      const newMapping = pullAt(lastMatchIndex, mappings);

      keyCodeMappings[item.key] = newMapping;
      setKeyCodeMappings(keyCodeMappings);
   };

   const pushMappings = (mappings: KeypressMapping[]) => {
      mappings.forEach(mapping => {
         pushMapping(mapping);
      });
   };

   const popMappings = (mappings: KeypressMapping[]) => {
      mappings.forEach(mapping => {
         popMapping(mapping);
      });
   };

   const keypressContext = {
      pushMapping,
      popMapping,
      pushMappings,
      popMappings,
   };

   return <KeypressContext.Provider value={keypressContext}>{children}</KeypressContext.Provider>;
}

export { KeypressActionProvider, KeypressContext };
